import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, KeyRound, AlertCircle, CheckCircle2, ShoppingCart } from "lucide-react";
import { Link } from "react-router-dom";

interface TokenEntryProps {
  onValidToken: (code: string) => void;
}

export function TokenEntry({ onValidToken }: TokenEntryProps) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validated, setValidated] = useState(false);

  const handleValidate = async () => {
    if (!code.trim()) {
      setError("Please enter your access code");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke("validate-token", {
        body: { code: code.trim() },
      });

      if (invokeError) throw invokeError;

      if (data?.valid) {
        setValidated(true);
        setTimeout(() => {
          onValidToken(code.trim().toUpperCase());
        }, 500);
      } else {
        setError(data?.reason || "Invalid access code");
      }
    } catch (err: any) {
      console.error("Token validation error:", err);
      setError("Failed to validate code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !loading) {
      handleValidate();
    }
  };

  if (validated) {
    return (
      <Card className="max-w-md mx-auto">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 mx-auto mb-4 flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold mb-2">Access Validated</h3>
          <p className="text-muted-foreground">Starting your assessment...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <Card>
        <CardHeader className="text-center">
          <div className="w-14 h-14 rounded-full bg-primary/10 mx-auto mb-4 flex items-center justify-center">
            <KeyRound className="h-7 w-7 text-primary" />
          </div>
          <CardTitle className="text-2xl">Enter Your Access Code</CardTitle>
          <CardDescription>
            Enter the access code from your purchase confirmation email to begin the inventory.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="access-code">Access Code</Label>
            <Input
              id="access-code"
              placeholder="INV-XXXXXX"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setError(null);
              }}
              onKeyDown={handleKeyDown}
              className="text-center text-lg font-mono tracking-wider"
              disabled={loading}
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            onClick={handleValidate}
            disabled={loading || !code.trim()}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Validating...
              </>
            ) : (
              "Continue"
            )}
          </Button>
        </CardContent>
      </Card>

      <div className="mt-8 text-center p-6 bg-muted/30 rounded-lg">
        <p className="text-muted-foreground mb-3">Don't have an access code?</p>
        <Button variant="outline" asChild className="gap-2">
          <Link to="/purchase">
            <ShoppingCart className="h-4 w-4" />
            Purchase Access
          </Link>
        </Button>
      </div>
    </div>
  );
}
